import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import SearchBar from "../../common/components/input/SearchBar";
import { useEffect, useRef, useState } from "react";
import Translate from "../../common/translate/Translate";
import { CustomTable } from "../../common/components/table/CustomTable";
import PrimaryButton from "../../common/components/button/PrimaryButton";
import CustomIcon from "../../common/components/image/CustomIcon";
import styled from "styled-components";
import { FranchiseActionDropdownMenu } from "../../common/components/dropdown/FranchiseActionDropdownMenu";
import FranchiseAddModal from "./modals/FranchiseAddModal";
import { C3Text } from "../../common/components/text/CustomText";
import FranchiseEditModal from "./modals/FranchiseEditModal";
import { Theme } from "../../common/Theme";
import { useDevice } from "../../common/hooks/useDevice";
import FranchiseService from "../../service/FranchiseService";
import { useSearchParams } from "react-router-dom";

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const ActionMenuCell = ({ row, rowIndex, dto, setOpenEditModal, setDto, onDelete }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const buttonRef = useRef(null);

    return (
        <SizedBox width={"auto"} height={"auto"} padding={"0 24px"} cursor={"pointer"}>
            <MenuContainer ref={buttonRef}>
                <CustomIcon
                    iconName={"list_action"}
                    size={"24px"}
                    cursor={"pointer"}
                    onClick={() => setIsMenuOpen(true)}
                />
                {isMenuOpen && (
                    <FranchiseActionDropdownMenu
                        anchorEl={buttonRef.current}
                        onClose={() => setIsMenuOpen(false)}
                        placement="bottom-end"
                        dto={dto}
                        setOpenEditModal={setOpenEditModal}
                        setDto={setDto}
                        onDelete={onDelete}
                    />
                )}
            </MenuContainer>
        </SizedBox>
    );
};

function FranchiseListPage() {
    const PAGE_SIZE = 8;
    const service = new FranchiseService();
    const [searchParams, setSearchParams] = useSearchParams();

    // 현재 페이지/검색 조건의 단일 출처(single source of truth)는 URL이다.
    const page = Number(searchParams.get("page")) || 1;
    const committedType = searchParams.get("searchType") || "franchiseCode";
    const committedValue = searchParams.get("searchValue") || "";

    // 검색 입력값은 "확정 전" 로컬 상태로만 보관 (검색 버튼을 눌러야 URL에 반영)
    const [searchFilter, setSearchFilter] = useState(committedType);
    const [searchValue, setSearchValue] = useState(committedValue);
    const [total, setTotal] = useState(0);

    // list data
    const [franchiseList, setFranchiseList] = useState([]);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const getFranchiseList = (page, pageSize, searchType, searchValue) => {
        const params =
            searchType === "franchiseName"
                ? { page, pageSize, franchiseCode: "", franchiseName: searchValue }
                : { page, pageSize, franchiseCode: searchValue, franchiseName: "" };

        service
            .getFranchiseList(params)
            .then((result) => {
                if (!isMountedRef.current) return;
                setFranchiseList(result.franchiseInfo);
                setTotal(result.page.totalCount);
                // page/검색 조건은 절대 다시 쓰지 않는다. 조회 결과인 totalPage만 갱신.
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set("totalPage", result.page.totalPage);
                        return next;
                    },
                    { replace: true }
                );
            })
            .catch((error) => {
                console.error("Error fetching franchise list:", error);
            });
    };

    // URL(page/검색 조건)이 바뀔 때만 목록을 조회한다.
    useEffect(() => {
        getFranchiseList(page, PAGE_SIZE, committedType, committedValue);
    }, [page, committedType, committedValue]);

    const onSearch = () => {
        // 입력값을 URL에 확정 반영하고 1페이지로 이동 → 위 useEffect가 조회를 트리거
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("page", "1");
                next.set("searchType", searchFilter);
                next.set("searchValue", searchValue);
                return next;
            },
            { replace: true }
        );
    };

    const handleSave = () => {
        getFranchiseList(page, PAGE_SIZE, committedType, committedValue);
    };

    // edit modal
    const [openEditModal, setOpenEditModal] = useState(false);
    const [dto, setDto] = useState({ code: "", name: "" });

    // for table
    const tableHeaders = [
        {
            key: "franchiseCode",
            label: "Franchise Code",
            render: (row) => <C3Text fontColor={Theme.color.indigo500}>{row.franchiseCode}</C3Text>,
        },
        {
            key: "name",
            label: "Franchise Name",
            textAlign: "start",
            whiteSpace: "normal",
            minWidth: "388px",
        },
        {
            key: "created_at",
            label: Translate("common.registeredDate"),
        },
        {
            key: "updated_at",
            label: "Updated Date",
        },
        {
            key: "menu",
            label: "Action",
            render: (row, rowIndex) => (
                <ActionMenuCell
                    row={row}
                    rowIndex={rowIndex}
                    dto={{
                        idFranchise: row.idFranchise,
                        countryCode: row.countryCode,
                        code: row.franchiseCode,
                        name: row.name,
                        timezone: row.timezone,
                        maxTicketNo: row.maxTicketNo,
                        registeredDate: row.created_at,
                        updatedDate: row.updated_at,
                    }}
                    setOpenEditModal={setOpenEditModal}
                    setDto={setDto}
                    onDelete={() => getFranchiseList(page, PAGE_SIZE, committedType, committedValue)}
                />
            ),
        },
    ];

    // add modal
    const [openAddModal, setOpenAddModal] = useState(false);

    const currentDevice = useDevice();

    let addButton = <PrimaryButton icon={"add_white"} text={"Add Franchise"} onClick={() => setOpenAddModal(true)} />;

    if (currentDevice === "mobile") {
        addButton = <PrimaryButton icon={"add_white"} onlyIcon onClick={() => setOpenAddModal(true)} />;
    }
    const searchBar = (
        <SearchBar
            type={"franchiseList"}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            totalWidth={currentDevice === "mobile" ? "100%" : undefined}
            selectorWidth={currentDevice === "mobile" ? "20%" : undefined}
            inputWidth={currentDevice === "mobile" ? "70%" : undefined}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSearchButtonClick={onSearch}
        />
    );
    let pageTitle = <PageTitle title={"Franchise Management"} contentLength={total} />;
    if (currentDevice === "mobile") {
        pageTitle = <PageTitle title={"Franchise Management"} contentLength={total} button={addButton} />;
    }

    return (
        <CommonContentWrapper hasPagination>
            <Padding padding={"24px 20px"}>
                {pageTitle}
                <SizedBox height={"24px"} />
                {currentDevice === "mobile" && <>{searchBar}</>}
                {currentDevice !== "mobile" && (
                    <Row justifyContent={"space-between"}>
                        {searchBar}
                        <SizedBox width={"13px"} />
                        {addButton}
                    </Row>
                )}
                <SizedBox height={"20px"} />
                <CustomTable headers={tableHeaders} data={franchiseList} hasColumnBorder={false} />
            </Padding>
            {openAddModal && (
                <FranchiseAddModal isOpen={openAddModal} onClose={() => setOpenAddModal(false)} onSave={handleSave} />
            )}
            {openEditModal && (
                <FranchiseEditModal
                    isOpen={openEditModal}
                    onClose={() => setOpenEditModal(false)}
                    onSave={handleSave}
                    dto={dto}
                />
            )}
        </CommonContentWrapper>
    );
}

export default FranchiseListPage;
